<script setup>
/**
 * Troca a corporação ativa sem sair da plataforma.
 *
 * Até aqui a corporação era escolhida no login e não mudava mais: o admin
 * mandava no handoff do SSO e nenhuma tela chamava o `setCorp`, que já existia
 * no serviço. Quem precisava operar em outra empresa tinha que encerrar a
 * sessão e entrar de novo.
 *
 * A lista sai do `listCorps`, que lê as corporações do próprio usuário do
 * `localStorage.credentials` -- não é chamada de API, e por isso o botão abre
 * instantâneo.
 *
 * Só aparece com mais de uma corporação: para quem tem uma só, um seletor de
 * um item é ruído.
 */
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { listCorps, setCorp } from "@/services/api/corporation.js";
import { handleError } from "@/utils/errors";

const props = defineProps({
    currentCorp: { type: Object, default: null },
});

const { t } = useI18n();
const corporations = ref([]);
const switching = ref(null);

const canSwitch = computed(() => corporations.value.length > 1);

const currentName = computed(
    () => props.currentCorp?.companyName || props.currentCorp?.company_name || ""
);

async function switchTo(corp) {
    if (switching.value || corp.id === props.currentCorp?.id) return;

    try {
        switching.value = corp.id;
        await setCorp({ id: corp.id });

        // Recarga inteira, e não só um refresh de estado: a corporação decide
        // permissão, menu e os dados de toda tela aberta. Repintar por partes
        // deixaria pedaços da empresa anterior na tela, que é pior do que
        // esperar dois segundos.
        window.location.reload();
    } catch (error) {
        switching.value = null;
        message.error(await handleError(error, t));
    }
}

onMounted(async () => {
    try {
        corporations.value = await listCorps({});
    } catch {
        // Sem a lista o botão simplesmente não aparece, e o cabeçalho segue
        // mostrando a corporação atual.
        corporations.value = [];
    }
});
</script>

<template>
    <v-menu v-if="canSwitch" location="bottom start">
        <template #activator="{ props: activator }">
            <button
                v-bind="activator"
                class="switch-corp"
                :title="t('common.topHeader.switchCorporation')"
                type="button"
            >
                <span class="material-symbols-outlined">unfold_more</span>
            </button>
        </template>

        <v-list density="compact" class="corp-list">
            <v-list-subheader>{{ t('common.topHeader.switchCorporation') }}</v-list-subheader>
            <v-list-item
                v-for="corp in corporations"
                :key="corp.id"
                :active="corp.id === currentCorp?.id"
                :disabled="switching !== null"
                @click="switchTo(corp)"
            >
                <template #prepend>
                    <span
                        class="material-symbols-outlined mark"
                        :class="{ invisible: corp.id !== currentCorp?.id }"
                    >check</span>
                </template>
                <v-list-item-title>{{ corp.companyName }}</v-list-item-title>
                <template #append>
                    <v-progress-circular
                        v-if="switching === corp.id"
                        indeterminate
                        size="16"
                        width="2"
                    />
                </template>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<style lang="scss" scoped>
.switch-corp {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    padding: 2px;
    border: none;
    background: transparent;
    color: var(--secondaryColor, #6b7280);
    cursor: pointer;
    border-radius: 4px;
    line-height: 1;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    .material-symbols-outlined {
        font-size: 20px;
    }
}

.corp-list {
    min-width: 240px;

    .mark {
        font-size: 18px;
        margin-right: 6px;
    }

    // Mantém o alinhamento: esconder com v-if faria o texto dançar.
    .invisible {
        visibility: hidden;
    }
}
</style>
